import React from "react";
import {
  FaLink,
  FaPlus,
  FaSave,
  FaSyncAlt,
  FaTrash,
  FaVideo,
} from "react-icons/fa";

const MAX_CATEGORIES = 6;

const EMPTY_SLOT = (slotIndex) => ({
  slot_index: slotIndex,
  label: "",
  note: "",
  is_active: true,
});

const SURFACE_TYPES = [
  {
    value: "MAT",
    label: "Tapis de compétition",
  },
  {
    value: "CENTRAL_TABLE",
    label: "Table centrale",
  },
  {
    value: "PODIUM",
    label: "Podium",
  },
  {
    value: "OTHER",
    label: "Autre espace",
  },
];

const getDefaultSurfaceLabel = (
  surfaceType,
  selectedMat
) => {
  switch (surfaceType) {
    case "CENTRAL_TABLE":
      return "Table centrale";

    case "PODIUM":
      return "Podium";

    case "OTHER":
      return `Espace ${selectedMat}`;

    case "MAT":
    default:
      return `Tapis ${selectedMat}`;
  }
};

export default function MatEditor({
  loading,
  saving,
  savingStream,
  selectedMat,
  form,
  setForm,
  onSave,
  onSaveStream,
  onToggleStream,
  onDeleteStream,
  onAutoEmbed,
}) {
  const canHaveCategories =
    (form.surface_type || "MAT") === "MAT";

  const hasSavedStream = Boolean(
    form.stream_id
  );

  const updateSlot = (
    slotIndex,
    patch
  ) => {
    setForm((currentForm) => ({
      ...currentForm,

      slots: (
        currentForm.slots || []
      ).map((slot) =>
        Number(slot.slot_index) ===
        Number(slotIndex)
          ? {
              ...slot,
              ...patch,
            }
          : slot
      ),
    }));
  };

  const addCategory = () => {
    setForm((currentForm) => {
      const currentSlots =
        currentForm.slots || [];

      if (
        currentSlots.length >=
        MAX_CATEGORIES
      ) {
        return currentForm;
      }

      return {
        ...currentForm,

        slots: [
          ...currentSlots,
          EMPTY_SLOT(
            currentSlots.length + 1
          ),
        ],
      };
    });
  };

  const removeCategory = (slotIndex) => {
    setForm((currentForm) => ({
      ...currentForm,

      slots: (
        currentForm.slots || []
      )
        .filter(
          (slot) =>
            Number(slot.slot_index) !==
            Number(slotIndex)
        )
        .map((slot, index) => ({
          ...slot,
          slot_index: index + 1,
        })),
    }));
  };

  const handleSurfaceTypeChange = (
    surfaceType
  ) => {
    setForm((currentForm) => {
      const currentLabel = String(
        currentForm.label || ""
      ).trim();

      const genericLabels = [
        `Tapis ${selectedMat}`,
        `TAPIS ${selectedMat}`,
        `Espace ${selectedMat}`,
        "Table centrale",
        "Podium",
      ];

      const shouldUpdateLabel =
        !currentLabel ||
        genericLabels.includes(currentLabel);

      return {
        ...currentForm,

        surface_type: surfaceType,

        label: shouldUpdateLabel
          ? getDefaultSurfaceLabel(
              surfaceType,
              selectedMat
            )
          : currentForm.label,

        slots:
          surfaceType === "MAT"
            ? currentForm.slots?.length
              ? currentForm.slots
              : [1, 2, 3].map(EMPTY_SLOT)
            : currentForm.slots,
      };
    });
  };

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900 sm:p-6">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300">
            Surface sélectionnée
          </p>

          <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white">
            {form.label ||
              `Tapis ${selectedMat}`}
          </h2>
        </div>

        <button
          type="button"
          onClick={onSave}
          disabled={saving || loading}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 text-sm font-black text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FaSave />

          {saving
            ? "Enregistrement…"
            : "Enregistrer la surface"}
        </button>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300">
            Type de surface
          </label>

          <select
            value={
              form.surface_type || "MAT"
            }
            onChange={(event) =>
              handleSurfaceTypeChange(
                event.target.value
              )
            }
            className="mt-1 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950"
          >
            {SURFACE_TYPES.map(
              (surfaceType) => (
                <option
                  key={surfaceType.value}
                  value={surfaceType.value}
                >
                  {surfaceType.label}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300">
            Nom affiché
          </label>

          <input
            value={form.label || ""}
            onChange={(event) =>
              setForm((currentForm) => ({
                ...currentForm,
                label: event.target.value,
              }))
            }
            className="mt-1 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950"
          />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300">
            Ordre d’affichage
          </label>

          <input
            type="number"
            min={1}
            max={30}
            value={form.sort_order}
            onChange={(event) =>
              setForm((currentForm) => ({
                ...currentForm,
                sort_order:
                  event.target.value,
              }))
            }
            className="mt-1 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950"
          />
        </div>

        <div className="md:col-span-3">
          <label className="inline-flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
            <input
              type="checkbox"
              checked={Boolean(
                form.is_open
              )}
              onChange={(event) =>
                setForm((currentForm) => ({
                  ...currentForm,
                  is_open:
                    event.target.checked,
                }))
              }
              className="h-4 w-4 accent-sky-600"
            />

            Surface ouverte et visible sur
            l’écran du dojo
          </label>
        </div>
      </div>

      {canHaveCategories ? (
        <div className="mt-7 border-t border-slate-200 pt-6 dark:border-white/10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-black text-slate-950 dark:text-white">
                Catégories affichées
              </h3>

              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Elles apparaîtront dans cet
                ordre sur l’écran.
              </p>
            </div>

            <button
              type="button"
              onClick={addCategory}
              disabled={
                (form.slots || []).length >=
                MAX_CATEGORIES
              }
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-sky-100 px-4 text-sm font-black text-sky-700 hover:bg-sky-200 disabled:opacity-40 dark:bg-sky-500/15 dark:text-sky-300"
            >
              <FaPlus />
              Ajouter une catégorie
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {(form.slots || []).map(
              (slot, index) => (
                <article
                  key={slot.slot_index}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-wider text-sky-700 dark:text-sky-300">
                      Catégorie {index + 1}
                    </p>

                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 text-xs font-bold">
                        <input
                          type="checkbox"
                          checked={Boolean(
                            slot.is_active
                          )}
                          onChange={(event) =>
                            updateSlot(
                              slot.slot_index,
                              {
                                is_active:
                                  event.target
                                    .checked,
                              }
                            )
                          }
                          className="accent-sky-600"
                        />

                        Afficher
                      </label>

                      <button
                        type="button"
                        onClick={() =>
                          removeCategory(
                            slot.slot_index
                          )
                        }
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-100 text-rose-600 hover:bg-rose-200 dark:bg-rose-500/15 dark:text-rose-300"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <input
                      value={slot.label || ""}
                      onChange={(event) =>
                        updateSlot(
                          slot.slot_index,
                          {
                            label:
                              event.target.value,
                          }
                        )
                      }
                      className="h-11 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950"
                      placeholder="Ex : M Benjamins -34 kg"
                    />

                    <input
                      value={slot.note || ""}
                      onChange={(event) =>
                        updateSlot(
                          slot.slot_index,
                          {
                            note:
                              event.target.value,
                          }
                        )
                      }
                      className="h-11 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950"
                      placeholder="Ex : Début à 10 h 30"
                    />
                  </div>
                </article>
              )
            )}
          </div>
        </div>
      ) : (
        <div className="mt-7 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 dark:border-white/15 dark:bg-white/5">
          <p className="font-black">
            Aucune catégorie nécessaire
          </p>

          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Les catégories sont uniquement
            utilisées pour les tapis de
            compétition.
          </p>
        </div>
      )}

      <details className="mt-7 rounded-2xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5">
        <summary className="flex cursor-pointer items-center gap-2 px-4 py-4 font-black">
          <FaVideo className="text-indigo-500" />
          Diffusion vidéo de cette surface
        </summary>

        <div className="border-t border-slate-200 p-4 dark:border-white/10">
          <div className="grid gap-4 md:grid-cols-2">
            <select
              value={
                form.stream_provider ||
                "YOUTUBE"
              }
              onChange={(event) =>
                setForm((currentForm) => ({
                  ...currentForm,
                  stream_provider:
                    event.target.value,
                }))
              }
              className="h-11 rounded-2xl border border-slate-200 bg-white px-3 dark:border-white/10 dark:bg-slate-950"
            >
              <option value="YOUTUBE">
                YouTube
              </option>
              <option value="TWITCH">
                Twitch
              </option>
              <option value="VIMEO">
                Vimeo
              </option>
              <option value="OTHER">
                Autre
              </option>
            </select>

            <input
              value={
                form.stream_title || ""
              }
              onChange={(event) =>
                setForm((currentForm) => ({
                  ...currentForm,
                  stream_title:
                    event.target.value,
                }))
              }
              className="h-11 rounded-2xl border border-slate-200 bg-white px-3 dark:border-white/10 dark:bg-slate-950"
              placeholder="Titre du live"
            />

            <input
              value={
                form.stream_url || ""
              }
              onChange={(event) =>
                setForm((currentForm) => ({
                  ...currentForm,
                  stream_url:
                    event.target.value,
                }))
              }
              className="h-11 rounded-2xl border border-slate-200 bg-white px-3 md:col-span-2 dark:border-white/10 dark:bg-slate-950"
              placeholder="Lien de la vidéo"
            />

            <input
              value={form.embed_url || ""}
              onChange={(event) =>
                setForm((currentForm) => ({
                  ...currentForm,
                  embed_url:
                    event.target.value,
                }))
              }
              className="h-11 rounded-2xl border border-slate-200 bg-white px-3 md:col-span-2 dark:border-white/10 dark:bg-slate-950"
              placeholder="URL embed"
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onAutoEmbed}
              className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-slate-200 px-3 text-xs font-black text-slate-800 dark:bg-white/10 dark:text-white"
            >
              <FaLink />
              Générer l’embed
            </button>

            <button
              type="button"
              onClick={onSaveStream}
              disabled={savingStream}
              className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-indigo-600 px-3 text-xs font-black text-white"
            >
              <FaSave />
              {savingStream
                ? "Enregistrement…"
                : "Enregistrer le live"}
            </button>

            {hasSavedStream ? (
              <>
                <button
                  type="button"
                  onClick={onToggleStream}
                  className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-amber-100 px-3 text-xs font-black text-amber-800"
                >
                  <FaSyncAlt />
                  Activer / arrêter
                </button>

                <button
                  type="button"
                  onClick={onDeleteStream}
                  className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-rose-600 px-3 text-xs font-black text-white"
                >
                  <FaTrash />
                  Supprimer le live
                </button>
              </>
            ) : null}
          </div>
        </div>
      </details>
    </section>
  );
}